import { memo, useCallback, useEffect, useState } from 'react'

import mixpanel from 'mixpanel-browser'

import { Point } from '../../../../../../api/types'
import { useBuildingContext } from '../../../../../../contexts/buildingContext'
import { useNavigation } from '../../../../../../hooks/useNavigation'
import { usePannellumViewer } from '../../../../../../hooks/usePannellum'
import { useDeepMemo } from '../../../../../../utils'
import { useImageViewerContext } from '../../../imageViewerContext'

import { resolveCameraPlanCoords } from './cameraPlanCoords'
import {
  getPitch,
  hasExplicitTourPitchCeiling,
  isWithinTourMaxRange,
  resolveTourMaxRangeFt,
  resolveTourPitchCeilingDeg,
  resolveTrustedTourInchesPerPixel,
  stableReferenceDistancePx,
} from './getPitch'
import { getTourPoints } from './getTourPoints'

/** Pixel distance range for oval size only — not floor-plan metrology. */
const OVAL_DISTANCE_RANGE_PX = 350

/** Camera height in inches for blue-dot pitch; fallback when image has no height. */
const DEFAULT_CAMERA_HEIGHT_INCHES = 48

interface TourManagerProps {
  viewerId: string
  /** Camera height in inches for blue-dot pitch; fallback used when missing. */
  height?: number
  /** Localized capture pose (plan pixels); falls back to planned point XY. */
  trueX?: string | number | null
  trueY?: string | number | null
  /**
   * Same degrees as Pannellum `angleOffset` (floor-plan ↔ panorama alignment).
   * Hotspot yaw is `geoYaw - this` — must stay identical to the viewer offset.
   */
  adjustedOffset?: number
}

export const TourManager = memo((props: TourManagerProps) => {
  const { state: buildingState, updateTour } = useBuildingContext()
  const { navigateToPointFullscreen } = useNavigation()
  const { updateMaster } = useImageViewerContext()
  const { loaded, addHotSpot, removeHotSpot } = usePannellumViewer(
    props.viewerId,
  )
  const [populated, setPopulated] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper tourPoints type
  const [tourPoints, setTourPoints] = useState<any[]>([])
  const [hotspotAngleOffset, setHotspotAngleOffset] = useState(0)

  const floorPlan = buildingState.floorData.latest_floor_plan as {
    scale?: number
    inches_per_pixel?: number | null
    scale_source?: string | null
  }
  const floorPlanScale = floorPlan?.scale
  const search =
    typeof window !== 'undefined' ? window.location.search : ''
  // Presentation clamp only when explicitly requested (geometric is default).
  const pitchCeilingDeg = hasExplicitTourPitchCeiling(search)
    ? resolveTourPitchCeilingDeg(search)
    : 0
  const maxRangeFt = resolveTourMaxRangeFt(search)

  // We need this because the application changes the points somehow and
  // somewhere without needing to. That was causing triggers to be fired
  // when the points were not changed. So we need to memoize the points
  // to avoid that.
  const memoizedPoints = useDeepMemo(buildingState.floorData.points)
  const memoizedPointData = useDeepMemo(buildingState.pointData)

  const hospotClickHandler = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper event type
      _event: any,
      args: {
        pitch: number
        yaw: number
        type: string
        tagId?: number
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper extras type
        extras?: any
      },
    ) => {
      if (args.extras) {
        const date = new URLSearchParams(window.location.search).get('date')
        if (date) {
          navigateToPointFullscreen(args.extras.pointId, { date: date })
        } else {
          navigateToPointFullscreen(args.extras.pointId)
        }
      }
      updateMaster({ angleDelta: null })
      mixpanel.track('Click Tour Hotspot')
    },
    [navigateToPointFullscreen, updateMaster],
  )

  useEffect(() => {
    const newPoints = getTourPoints(memoizedPoints, memoizedPointData)
    setTourPoints(prevTourPoints => {
      prevTourPoints.forEach((point: Point) => {
        const tagId = Number(point.point_id)
        removeHotSpot(tagId)
      })
      return newPoints
    })
    updateTour(newPoints)
  }, [memoizedPoints, memoizedPointData, removeHotSpot, updateTour])

  const populate = useCallback(
    (pointDataX, pointDataY) => {
      // Camera: localized pose when present (matches minimap frustum).
      const currentPointCoords = resolveCameraPlanCoords(
        pointDataX,
        pointDataY,
        props.trueX,
        props.trueY,
      )

      // Prefer capture height when provided (e.g. Dixon 90"); else 48".
      const heightUsed = props.height ?? DEFAULT_CAMERA_HEIGHT_INCHES
      const currentPointId = String(memoizedPointData?.point_id ?? '')

      // d_ref from planned neighbor XY (same frame as hotspots / minimap pins).
      const neighborhoodDistances: number[] = []
      for (const point of memoizedPoints as Point[]) {
        if (String(point.point_id) === currentPointId) continue
        if (!point.images?.length) continue
        const coords = resolveTourNeighborPlanCoords(point)
        const d = distanceBetween(coords, currentPointCoords)
        if (d > 0) neighborhoodDistances.push(d)
      }
      const referenceDistancePx = stableReferenceDistancePx(
        neighborhoodDistances,
      )

      // Legacy calibrated scale / floor_plan_scale always; NexLoc only with
      // ?tourUseIpp=1 (+ API include_untrusted_tour_metrology). Else H=height.
      const trustedInchesPerPixel = resolveTrustedTourInchesPerPixel({
        apiInchesPerPixel: floorPlan?.inches_per_pixel,
        scaleSource: floorPlan?.scale_source,
        legacyScale: floorPlanScale,
        search,
        referenceDistancePx,
      })

      tourPoints.forEach((point: Point) => {
        // Neighbors: planned waypoint XY — same pins the minimap shows.
        const neighborCoords = resolveTourNeighborPlanCoords(point)
        const distance = Math.round(
          distanceBetween(neighborCoords, currentPointCoords),
        )
        if (
          !isWithinTourMaxRange(distance, maxRangeFt, {
            inchesPerPixel: trustedInchesPerPixel,
            referenceDistancePx,
          })
        ) {
          return
        }
        const pitch = getPitch(distance, floorPlanScale, heightUsed, {
          inchesPerPixel: trustedInchesPerPixel,
          legacyScale: floorPlanScale,
          pitchCeilingDeg,
        })
        const yaw = planYawDegrees(currentPointCoords, neighborCoords)
        // Oval size from plan-pixel distance only — not ProjectFloorPlan.scale.
        const scaleY = limitNumberWithinRange(
          1 - distance / OVAL_DISTANCE_RANGE_PX,
          0.25,
          1,
        )
        const tagId = Number(point.point_id)
        addHotSpot(
          [pitch, yaw],
          'TOUR',
          hospotClickHandler,
          () => null,
          tagId,
          hotspotAngleOffset,
          {
            pointId: point.point_id,
            distance: distance,
            scaleY: scaleY,
            scaleX: scaleY + 1.4 - (1 - scaleY),
          },
        )
      })
    },
    [
      addHotSpot,
      floorPlan?.inches_per_pixel,
      floorPlan?.scale_source,
      floorPlanScale,
      hospotClickHandler,
      hotspotAngleOffset,
      maxRangeFt,
      memoizedPointData?.point_id,
      memoizedPoints,
      pitchCeilingDeg,
      props.height,
      props.trueX,
      props.trueY,
      search,
      tourPoints,
    ],
  )

  // Determines the actual hotspots angles
  useEffect(() => {
    if (props.adjustedOffset !== undefined) {
      setHotspotAngleOffset(props.adjustedOffset)
    } else {
      setHotspotAngleOffset(0)
    }
  }, [props.adjustedOffset])

  // Clean hotspots when the angle changes
  useEffect(() => {
    tourPoints.forEach((point: Point) => {
      const tagId = Number(point.point_id)
      removeHotSpot(tagId)
    })
    setPopulated(false)
  }, [hotspotAngleOffset, tourPoints, removeHotSpot])

  // When the panorama scene changes, loaded goes false then true. Reset populated
  // so we re-run populate() after the new image loads (fixes blue points disappearing after click).
  useEffect(() => {
    if (!loaded) setPopulated(false)
  }, [loaded])

  useEffect(() => {
    if (loaded && !populated && tourPoints.length > 0) {
      populate(memoizedPointData.x, memoizedPointData.y)
      setPopulated(true)
    }
  }, [
    loaded,
    memoizedPointData.x,
    memoizedPointData.y,
    populate,
    populated,
    tourPoints.length,
  ])

  return <></>
})

TourManager.displayName = 'TourManager'

// Util functions

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper point types
const distanceBetween = (p1: any, p2: any) => {
  return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2))
}

/**
 * Neighbor hotspot XY: planned waypoint only — matches minimap pins and
 * getTourPoints selection. Do not use image true_* here; that shortens
 * hallway-localized captures vs room-planned pins the user sees on the map.
 */
function resolveTourNeighborPlanCoords(point: Point): { x: number; y: number } {
  return { x: point.x, y: point.y }
}

function planYawDegrees(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper p1 type
  p1: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper p2 type
  p2: any,
): number {
  const deltaX = p2.x - p1.x
  const deltaY = p2.y - p1.y
  const rad = Math.atan2(deltaY, deltaX)
  let yaw = (rad / Math.PI) * 180 + (rad > 0 ? 0 : 360)
  yaw += 90 // arbitrary correction, but seems to work.
  return yaw
}

function limitNumberWithinRange(num: number, min: number, max: number) {
  const MIN = min || 1
  const MAX = max || 20
  return Math.min(Math.max(num, MIN), MAX)
}
